import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { CurrentUser, DeletionRequestsData } from '@core/account-types';
import { HttpApiService } from '@core/http-legacy';
import { decodeHTML } from '@core/utils-legacy';

import type { UserFormData, UserPasswordFormData } from './current-user.types';

@Injectable({ providedIn: 'root' })
export class CurrentUserHttpClient {
  protected readonly httpApi = inject(HttpApiService);

  public loadCurrentUser(): Observable<CurrentUser> {
    return this.httpApi.get('/admin', null, null).pipe(
      map(data => ({
        ...data.admin,
        mobileNotifications: data.admin.mobileNotifications,
        emailNotifications: data.admin.emailNotifications,
        companies: this.decodeCompanyNames(data.admin.companies),
      })),
    );
  }

  public updateCurrentUser(data: Partial<UserFormData>): Observable<CurrentUser> {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]: [string, string | boolean | File | undefined]) => {
      // eslint-disable-next-line eqeqeq
      if (value == undefined) return;

      const isFile = value instanceof File;
      formData.append(`admin[${key}]`, isFile ? <File>value : `${value}`);
    });

    return this.httpApi.patch('/admin/update_profile', {}, formData).pipe(
      map(adminResponse => ({
        ...adminResponse.admin,
      })),
    );
  }

  public updatePassword(formData: UserPasswordFormData): Observable<CurrentUser> {
    return this.httpApi
      .patch('/admin/update_password', {}, { admin: formData })
      .pipe(map(response => response.admin));
  }

  public accountDeletionRequests(): Observable<DeletionRequestsData> {
    return this.httpApi.get('/deletion_requests', {}, { skipCompany: true });
  }

  public deleteAccountRequest(secureChangeToken: string): Observable<void> {
    return this.httpApi.post(
      '/deletion_requests',
      {},
      { secureChangeToken },
      { skipCompany: true },
    );
  }
  private decodeCompanyNames(companies: CurrentUser['companies']): CurrentUser['companies'] {
    return companies.map(company => ({
      ...company,
      name: decodeHTML(company.name),
    }));
  }
}
